import React, { useMemo, useState } from 'react';
import baseStyles from '../../styles/ReporteCalificacionesStyles/ReporteCalificaciones.module.css';
import styles from '../../styles/MateriasEquivalentesStyles/MateriasEquivalentes.module.css';

const PAGE_SIZE = 10;
const UMBRAL_APROBATORIA = 7;

/**
 * Tabla editable de materias a revalidar.
 *
 * Props:
 *  - rows: array de materias candidatas (puede tener valores previos rev_*)
 *  - onChangeRow(id_materia, key, value)
 *  - readOnly: boolean (deshabilita inputs si true)
 */
function TablaMateriasRevalidar({ rows = [], onChangeRow, readOnly = false }) {
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, pageSafe]);

  const totalCreditos = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.creditos) || 0), 0),
    [rows]
  );
  const totalCapturadas = useMemo(
    () => rows.filter(r => r.rev_calificacion_final !== '' && r.rev_calificacion_final != null).length,
    [rows]
  );

  const renderEstado = (row) => {
    const cal = parseFloat(row.rev_calificacion_final);
    if (Number.isFinite(cal) && cal >= UMBRAL_APROBATORIA) {
      return <span className={styles.statusBadgeOk}>Aprobará</span>;
    }
    if (Number.isFinite(cal) && cal > 0) {
      return <span className={styles.statusBadgeFail}>Reprobada</span>;
    }
    if (row.tipo_curso_actual === 'EQUIVALENCIA') {
      return <span className={styles.statusBadgeOk}>Ya revalidada</span>;
    }
    return <span className={styles.statusBadgePending}>Pendiente</span>;
  };

  return (
    <>
      <div className={baseStyles.summaryStrip}>
        <div className={baseStyles.summaryItem}>
          <span className={baseStyles.summaryItemLabel}>Materias a revalidar</span>
          <span className={baseStyles.summaryItemValue}>{rows.length}</span>
        </div>
        <div className={baseStyles.summaryItem}>
          <span className={baseStyles.summaryItemLabel}>Créditos totales</span>
          <span className={baseStyles.summaryItemValue}>{totalCreditos}</span>
        </div>
        <div className={baseStyles.summaryItem}>
          <span className={baseStyles.summaryItemLabel}>Capturadas en pantalla</span>
          <span className={baseStyles.summaryItemValue}>{totalCapturadas}</span>
        </div>
      </div>

      <div className={baseStyles.tableWrapper}>
        <table className={baseStyles.reportTable}>
          <thead>
            <tr>
              <th>Clave</th>
              <th>Materia</th>
              <th className={baseStyles.cellCenter}>Créditos</th>
              <th className={baseStyles.cellCenter}>Año Ciclo Procedencia</th>
              <th className={baseStyles.cellCenter}>Calificación Revalidada</th>
              <th>Escuela de Procedencia</th>
              <th>Observaciones</th>
              <th className={baseStyles.cellCenter}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#999' }}>
                  No hay materias pendientes de revalidar para este estudiante.
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr key={row.id_materia}>
                  <td>{row.clave_materia}</td>
                  <td>{row.nombre_materia}</td>
                  <td className={baseStyles.cellCenter}>{row.creditos ?? 0}</td>
                  <td className={baseStyles.cellCenter}>
                    <input
                      type="text"
                      className={styles.cellInputAnio}
                      value={row.rev_anio_ciclo_escolar ?? ''}
                      maxLength={20}
                      disabled={readOnly}
                      placeholder="2022"
                      onChange={(e) => onChangeRow(row.id_materia, 'rev_anio_ciclo_escolar', e.target.value)}
                    />
                  </td>
                  <td className={baseStyles.cellCenter}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="10"
                      className={styles.cellInputNumber}
                      value={row.rev_calificacion_final ?? ''}
                      disabled={readOnly}
                      placeholder="8.50"
                      onChange={(e) => onChangeRow(row.id_materia, 'rev_calificacion_final', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className={styles.cellInput}
                      value={row.rev_escuela_procedencia ?? ''}
                      maxLength={200}
                      disabled={readOnly}
                      placeholder="Universidad XYZ"
                      onChange={(e) => onChangeRow(row.id_materia, 'rev_escuela_procedencia', e.target.value)}
                    />
                  </td>
                  <td>
                    <textarea
                      className={styles.cellTextarea}
                      value={row.rev_observaciones ?? ''}
                      disabled={readOnly}
                      onChange={(e) => onChangeRow(row.id_materia, 'rev_observaciones', e.target.value)}
                    />
                  </td>
                  <td className={baseStyles.cellCenter}>{renderEstado(row)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {rows.length > PAGE_SIZE && (
        <div className={baseStyles.pagination}>
          <span className={baseStyles.pageInfo}>
            Página {pageSafe} de {totalPages} — {rows.length} registros
          </span>
          <div className={baseStyles.pageButtons}>
            <button
              type="button"
              className={baseStyles.pageButton}
              onClick={() => setPage(1)}
              disabled={pageSafe <= 1}
            >« Primera</button>
            <button
              type="button"
              className={baseStyles.pageButton}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={pageSafe <= 1}
            >‹ Anterior</button>
            <button
              type="button"
              className={baseStyles.pageButton}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={pageSafe >= totalPages}
            >Siguiente ›</button>
            <button
              type="button"
              className={baseStyles.pageButton}
              onClick={() => setPage(totalPages)}
              disabled={pageSafe >= totalPages}
            >Última »</button>
          </div>
        </div>
      )}
    </>
  );
}

export default TablaMateriasRevalidar;
